import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

interface TextToggleOption {
  value: string;
  label: ReactNode;
}

export interface TextToggleProps {
  options: TextToggleOption[];
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  disabled?: boolean;
  className?: string;
}

const textToggleVariants = variants({
  slots: {
    root: cn('inline-flex items-center gap-sm rounded-sm p-xs', 'bg-default'),
    option: cn(
      'cursor-pointer rounded-sm px-md py-sm',
      'text-body-sm font-semibold text-subtle',
      'transition-colors',
    ),
  },
});

const activeOptionClass = cn(
  'bg-gradient-to-b from-[#363636] to-[#292929]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]',
  'cursor-default text-tertiary-default',
);

export const TextToggle = forwardRef(
  (
    { options, value, onChange, name, disabled, className }: TextToggleProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [selectedValue, setSelectedValue] = useState(
      value ?? options[0]?.value,
    );

    useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value);
      }
    }, [value]);

    const { root, option } = textToggleVariants();

    const handleSelect = (optionValue: string) => {
      if (disabled || optionValue === selectedValue) {
        return;
      }

      setSelectedValue(optionValue);
      onChange?.(optionValue);
    };

    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label={name}
        className={cn(root(), disabled && 'opacity-stronger', className)}
      >
        {options.map((opt) => {
          const isActive = selectedValue === opt.value;

          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              className={cn(
                option(),
                isActive && activeOptionClass,
                disabled && 'cursor-not-allowed',
              )}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  },
);

TextToggle.displayName = 'TextToggle';
